import React, { ReactNode, useMemo } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import BaseModal, { type BaseModalFooterAction } from './BaseModal'
import { ListToolbarNeutralButton } from '@/components/common/listToolbar'
import { useI18n } from '@/contexts/I18nContext'
import { banhangKiotLike } from '@/components/layout/banhangKiotTokens'

interface BaseModalImportProps {
  open: boolean
  onClose: () => void
  onSave: () => void
  loading?: boolean
  /** Text dưới spinner khi đang loading (vd tiến độ upload). */
  loadingText?: string
  disabled?: boolean
  title?: string
  description?: ReactNode
  fileName?: string
  onChooseFile?: () => void
  /** Nội dung phụ dưới vùng chọn file (options, kết quả, …) */
  children?: ReactNode
  templateComponent?: ReactNode
  closeLabel?: string
  saveLabel?: string
  chooseFileLabel?: string
  chooseFileAriaLabel?: string
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  footerActions?: BaseModalFooterAction[]
  hideFilePicker?: boolean
}

const BaseModalImport: React.FC<BaseModalImportProps> = ({
  open,
  onClose,
  onSave,
  loading = false,
  loadingText,
  disabled = false,
  title = 'Import',
  description,
  fileName = '',
  onChooseFile,
  children,
  templateComponent,
  closeLabel,
  saveLabel,
  chooseFileLabel,
  chooseFileAriaLabel,
  maxWidth = 'sm',
  footerActions: footerActionsOverride,
  hideFilePicker = false,
}) => {
  const { common: commonT } = useI18n()
  const closeText = closeLabel ?? commonT('actions.cancel')
  const saveText = saveLabel ?? commonT('actions.importData')
  const chooseText = chooseFileLabel ?? commonT('actions.select')
  const chooseAria = chooseFileAriaLabel ?? chooseText

  const defaultFooterActions: BaseModalFooterAction[] = useMemo(
    () => [
      {
        label: closeText,
        onClick: onClose,
        variant: 'outlined' as const,
        startIcon: <CloseOutlinedIcon sx={{ fontSize: 20 }} />,
        disabled: loading,
      },
      {
        label: saveText,
        onClick: onSave,
        variant: 'contained' as const,
        color: 'primary' as const,
        startIcon: <FileUploadIcon sx={{ fontSize: 20 }} />,
        disabled: disabled || loading,
      },
    ],
    [closeText, disabled, loading, onClose, onSave, saveText],
  )

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      title={title}
      headerIntent="create"
      closeButtonAriaLabel={commonT('actions.close')}
      footerActions={footerActionsOverride ?? defaultFooterActions}
      paperSx={{ minHeight: hideFilePicker ? 280 : 220 }}
      contentSx={{ minHeight: loading ? 140 : undefined }}
    >
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1.5,
            minHeight: 140,
            py: 2,
          }}
        >
          <CircularProgress size={36} />
          {loadingText ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontFamily: banhangKiotLike.kiotFontStack,
                fontSize: banhangKiotLike.kiotFontSizePx,
                textAlign: 'center',
              }}
            >
              {loadingText}
            </Typography>
          ) : null}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!hideFilePicker ? (
            <>
              {description ? (
                typeof description === 'string' ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontFamily: banhangKiotLike.kiotFontStack,
                      fontSize: banhangKiotLike.kiotFontSizePx,
                      lineHeight: banhangKiotLike.kiotLineHeight,
                    }}
                  >
                    {description}
                  </Typography>
                ) : (
                  description
                )
              ) : null}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {onChooseFile ? (
                  <ListToolbarNeutralButton
                    startIcon={<CloudUploadIcon />}
                    onClick={onChooseFile}
                    aria-label={chooseAria}
                  >
                    {chooseText}
                  </ListToolbarNeutralButton>
                ) : null}
                {templateComponent}
              </Box>
              {fileName ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: banhangKiotLike.kiotFontStack,
                    fontSize: banhangKiotLike.kiotFontSizePx,
                    color: banhangKiotLike.kiotTextPrimary,
                  }}
                >
                  {commonT('importModal.fileSelected', { name: fileName })}
                </Typography>
              ) : null}
            </>
          ) : null}
          {children}
        </Box>
      )}
    </BaseModal>
  )
}

export default BaseModalImport
